import React from 'react';
import { Callout, Step, DocHeading, DocTable, Shot } from './components';

// A text the bot shows on screen, quoted exactly.
function Msg({ children, red }) {
  return (
    <span className={`font-mono text-xs ${red ? 'text-red-400' : 'text-gray-300'}`}>{children}</span>
  );
}

function PageAutoMelt({ navigate }) {
  return (
    <div>
      <p className="text-sm text-brand-400 font-medium mb-2">Farm</p>
      <h1 className="font-display text-3xl font-bold text-white tracking-tight mb-3">
        Melt &amp; Cubes
      </h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        Melting turns the contents of your bag into dust and materials without a merchant: runes
        into rune dust, gear into runes of power, gems into shiny dust. The same tab also opens the
        Mystic cubes you collect. Everything is on the{' '}
        <strong className="text-white">FARM &rsaquo; Melt</strong> tab.
      </p>

      <Callout type="danger">
        <strong>Melting cannot be undone either.</strong> Same discipline as selling: Dry run is on
        by default, the defaults are narrow, and locked slots are never touched.
      </Callout>

      <DocHeading>The Safe Way to Start</DocHeading>
      <Step n={1} title="Leave Dry run on">
        <strong>Dry run</strong> sits under <strong>ADVANCED</strong> on the <strong>MELT</strong>{' '}
        card. While it is on, the buttons read <strong>PREVIEW RUNES</strong>,{' '}
        <strong>PREVIEW ITEMS</strong>, <strong>PREVIEW GEMS</strong> and{' '}
        <strong>PREVIEW CUBES</strong>, and nothing is melted.
      </Step>
      <Step n={2} title="Lock what must never go">
        On{' '}
        <button onClick={() => navigate('bag')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">FARM &rsaquo; Bag</button>,
        lock the slots that hold runes, gems and gear you want to keep.
      </Step>
      <Step n={3} title="Set WHAT TO MELT">
        Choose the gear rarities, the gem grades and how many of each gem to keep.
      </Step>
      <Step n={4} title="Press WHAT WOULD GO">
        A list window shows every unlocked slot, which of the four buttons would take it, and why.
      </Step>
      <Step n={5} title="Only then turn Dry run off">
        The buttons change to <strong>MELT RUNES</strong>, <strong>MELT ITEMS</strong>,{' '}
        <strong>MELT GEMS</strong> and <strong>OPEN CUBES</strong>.
      </Step>

      <Shot
        name="auto-melt-01-melt-tab.png"
        tab="FARM › Melt"
        caption="The Melt tab: the four melt buttons with their keys on the left, what to melt, the status and what is in the bag on the right."
        shoot="A FARM › Melt fül teljes nézete, Dry run BEKAPCSOLVA (ADVANCED csukva, látszik a narancs „Dry run is on (Advanced) - nothing is actually melted.” sor). Bal oldalt a MELT kártya a négy PREVIEW gombbal két oszlopban, mindegyik gomb alatt a saját billentyű-sorával (Rune key / Item key / Gem key / Cube key), alattuk az Auto melt kapcsoló, az ADVANCED lenyíló és a WHAT WOULD GO gomb; alatta a CUBES kártya. Jobb oldalt a WHAT TO MELT, a STATUS és az IN THE BAG kártya, az utóbbiban legyen néhány rúna, felszerelés és kő. Az új, ikonos felső sáv látsszon."
      />

      <DocHeading>The Four Buttons</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        Each button is a separate operation, because the game handles them separately. Every button
        has its own key right under it.
      </p>
      <DocTable
        headers={['Button', 'What it takes', 'What you get', 'Key']}
        rows={[
          [<strong className="text-white">MELT RUNES</strong>, 'Every rune in an unlocked slot, whatever its grade.', 'Rune dust', 'Rune key'],
          [<strong className="text-white">MELT ITEMS</strong>, 'Gear of the rarities you chose — unidentified, and identified too if you allowed it.', 'Runes of power', 'Item key'],
          [<strong className="text-white">MELT GEMS</strong>, 'Gems of the grades you chose, above the amount you keep.', 'Shiny dust', 'Gem key'],
          [<strong className="text-white">OPEN CUBES</strong>, 'The Mystic cubes in your bag.', 'Whatever is inside', 'Cube key'],
          [<strong className="text-white">STOP</strong>, 'Appears while an operation is running.', '—', '—'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        A key starts its operation (a preview while Dry run is on) and, pressed again, stops it. Keys
        only react while the game window is in front, and they need no other switch. If you stop an
        operation half-way, a pop-up tells you how many items were left in the plan. A key that is
        easy to hit by accident (Caps Lock, Shift, Ctrl, Alt, Space, Enter and similar) gets a red
        warning on the card.
      </p>
      <Callout type="warning">
        <strong>Runes have no filter.</strong> MELT RUNES takes every rune in every unlocked slot, of
        every grade &mdash; the card says so itself:{' '}
        <Msg>Runes have no filter: melting them does not sort by grade.</Msg> Lock the slots of the
        runes you want to keep.
      </Callout>

      <DocHeading>What to Melt</DocHeading>
      <DocTable
        headers={['Setting', 'What it does']}
        rows={[
          [<strong className="text-white">Unidentified gear</strong>, 'Which rarities MELT ITEMS may take from unidentified gear: Uncommon, Magic, Rare, Epic, Unique, Set, Mythic. Default: Uncommon and Magic. There is no Common entry: unidentified Common gear, and unidentified gear whose rarity the bot cannot read, is always taken by MELT ITEMS — this list cannot hold it back.'],
          [<strong className="text-white">Melt identified gear too</strong>, 'Off by default. Turn it on only if you also want MELT ITEMS to take identified gear — the gear you already know the value of.'],
          [<strong className="text-white">Identified gear</strong>, <>Appears once the switch above is on: its own rarity list, separate from the unidentified one (default Uncommon and Magic). Identified gear with an unreadable rarity is never melted, and neither is identified Common gear, because the bot cannot tell Common from an unreadable rarity &mdash; the card says <Msg>Identified gear with an unreadable rarity is never melted - the rarity IS the value.</Msg></>],
          [<strong className="text-white">Gem grade</strong>, 'Which gem grades MELT GEMS may take, from a (Rough) to i (Royal). Default: the two lowest.'],
          [<strong className="text-white">Keep per gem type</strong>, 'How many of each gem type to leave (0–50, default 1). It counts all stacks of that gem together: with 999 + 201 of one gem and Keep 1, one gem is left in total, not one per stack.'],
        ]}
      />
      <Callout type="info">
        <strong>Some things are never melted.</strong> Jewels are left alone &mdash; the game treats
        them as their own kind, and they are worth more than dust. Gems above grade i (the Trapezoid,
        Imperial and Square grades) are not taken by MELT GEMS. Gems in locked slots do not count
        toward <em>Keep per gem type</em>, so a locked stack never makes the bot take more from your
        unlocked ones.
      </Callout>

      <DocHeading>Auto Melt</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The four buttons are one-shot. The <strong className="text-white">Auto melt</strong> switch
        on the <strong className="text-white">MELT</strong> card runs the rune, item and gem passes
        for you while you farm.
      </p>
      <DocTable
        headers={['Setting', 'What it does']}
        rows={[
          [<strong className="text-white">Auto melt</strong>, 'Shortly after the bot picks something up, it runs the ticked passes one after another — at most every 8 seconds. It never interrupts a pass that is already running. When you switch it on (with Dry run off), it does one round about a second later, without waiting for a pickup.'],
          [<><strong className="text-white">Runes</strong>, <strong className="text-white">Items</strong>, <strong className="text-white">Gems</strong></>, 'Shown under the switch: which passes it runs by itself. All three are ticked by default. The filters are the same as for the buttons.'],
        ]}
      />
      <Callout type="warning">
        <strong>Auto melt obeys Dry run.</strong> With Dry run on it melts nothing, and the card says{' '}
        <Msg>Dry run is on - it will not melt anything on its own either.</Msg> With all three
        unticked you see <Msg>Nothing is ticked - it will not melt anything.</Msg> Remember that
        with <em>Runes</em> ticked, every rune in an unlocked slot goes after each pickup. &ldquo;After a
        pickup&rdquo; means pickups made by the bot (<button onClick={() => navigate('auto-loot')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Auto Loot</button>), not items you pick up by hand.
      </Callout>

      <DocHeading>Cubes</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The <strong className="text-white">CUBES</strong> card has its own switch, because opening a
        cube is not melting: it costs nothing and cannot destroy anything you own. Only Mystic cubes
        are opened &mdash; event chests, premium packs and other boxes are never touched.
      </p>
      <DocTable
        headers={['Setting', 'What it does']}
        rows={[
          [<strong className="text-white">Open cubes automatically</strong>, 'Opens your Mystic cubes shortly after the bot picks something up, at most every 5 seconds. It waits while your bag is full, because the reward needs a free slot. Like everything here, it does nothing while Dry run is on.'],
          [<><strong className="text-white">ADVANCED</strong> &rsaquo; <strong className="text-white">Per burst</strong></>, 'How many cubes it opens at once (1–20, default 5).'],
          [<><strong className="text-white">ADVANCED</strong> &rsaquo; <strong className="text-white">Delay</strong></>, 'The pause between bursts (30–1000 ms, default 100). The defaults are fine; slow down if the game starts to stutter.'],
        ]}
      />

      <DocHeading>The WHAT WOULD GO Window</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The <strong className="text-white">WHAT WOULD GO</strong> button (it becomes{' '}
        <strong className="text-white">HIDE THE LIST</strong>) opens a separate window that stays
        open with the menu closed. It lists every unlocked slot with <strong>MELT</strong> or{' '}
        <strong>keep</strong> and the reason. The top line is green while Dry run is on (<Msg>DRY RUN is ON - this is a preview, nothing is melted.</Msg>) and red when it is off (<Msg red>DRY RUN is OFF - the four buttons WILL take these for real.</Msg>). It works the
        same way as the list on{' '}
        <button onClick={() => navigate('auto-sell')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Auto Sell</button>:{' '}
        <strong className="text-white">Refresh now</strong>, <strong className="text-white">Close</strong>{' '}
        and <strong className="text-white">Show keep items too</strong>.
      </p>
      <DocTable
        headers={['Reason', 'What it means']}
        rows={[
          [<Msg>{'rune - MELT RUNES takes it'}</Msg>, 'This rune goes when you press MELT RUNES.'],
          [<Msg>{'unidentified gear - MELT ITEMS takes it'}</Msg>, <>Goes with MELT ITEMS (or <Msg>identified gear - …</Msg>).</>],
          [<Msg>{'gem: stock N, keep K -> M melted'}</Msg>, <>How many of this gem you have, how many stay, and how many MELT GEMS takes. It reads <Msg>{'-> nothing'}</Msg> when the stock is at or below the keep amount.</>],
          [<Msg>{'cube - OPEN CUBES opens N'}</Msg>, 'How many cubes of this stack would be opened.'],
          [<Msg>{"gem: tier 'x' is not selected"}</Msg>, <>That gem grade is not ticked (<Msg>{"is not meltable"}</Msg> means the grade can never be melted).</>],
          [<Msg>{'rarity N is not selected'}</Msg>, 'That gear rarity is not ticked. The number is the rarity: 1 Uncommon, 2 Magic, 3 Rare, 4 Epic, 5 Unique, 6 Set, 7 Mythic.'],
          [<Msg>{'rarity is unreadable - not touched'}</Msg>, 'Identified gear whose rarity cannot be read, or identified Common gear. It is never melted.'],
          [<Msg>{'identified gear is off'}</Msg>, 'Melt identified gear too is switched off.'],
          [<Msg>{'no melt rule matches (category N)'}</Msg>, 'None of the four buttons handles this kind of item.'],
        ]}
      />

      <DocHeading>Status and In the Bag</DocHeading>
      <DocTable
        headers={['Line', 'What it means']}
        rows={[
          [<strong className="text-white">Status</strong>, <>What it is doing right now, for example <Msg>Reading the bag...</Msg>, <Msg>Melting runes...</Msg>, <Msg>Opening cubes...</Msg>, <Msg>Done</Msg> or <Msg>Dry run</Msg>.</>],
          [<strong className="text-white">In the plan</strong>, <>How many items the current operation covers. <Msg>(more follows)</Msg> means the plan was full: after this round it reads the bag again and continues by itself.</>],
          [<strong className="text-white">Progress</strong>, 'Where it is in that plan, while running.'],
          [<strong className="text-white">Sent</strong>, 'How many rune and gem melt commands went out in the last run. This line (and the two below) only appears after a rune or gem run that had something to report.'],
          [<strong className="text-white">Held back</strong>, 'Runes or gems the last check kept, because something changed after planning (you locked the slot, moved the item, changed a setting).'],
          [<strong className="text-white">Refused</strong>, 'Commands the game did not accept, with the last reason.'],
          [<strong className="text-white">Runes melted / Items melted / Cubes opened</strong>, 'Session counters. Runes melted counts every rune and gem melt command; Items melted counts gear pieces.'],
          [<strong className="text-white">IN THE BAG</strong>, <>What the buttons would work on right now, from unlocked slots only: <strong>Runes</strong>, <strong>Cubes</strong>, <strong>Unidentified gear</strong> (it reads <strong>Gear to melt</strong> when identified gear is on), <strong>Gems (types)</strong>, and <strong>Would melt</strong> &mdash; how many gems go after the keep amount. Below that, each gem type with its stock and how many would go.</>],
        ]}
      />

      <DocHeading>When It Does Not Melt</DocHeading>
      <DocTable
        headers={['You see', 'What it means']}
        rows={[
          [<Msg>Dry run</Msg>, 'Dry run is on (under ADVANCED). Nothing is sent.'],
          [<><Msg>No runes in the bag</Msg>, <Msg>No cubes in the bag</Msg></>, 'Nothing of that kind in your unlocked slots.'],
          [<Msg>No gems above the keep amount</Msg>, 'Every selected gem type is at or below Keep per gem type. Check the gem list in IN THE BAG.'],
          [<Msg>Nothing matches the filter</Msg>, 'No gear of the ticked rarities. Open WHAT WOULD GO for the reasons.'],
          [<Msg red>The inventory could not be read by slot - nothing would be melted.</Msg>, <>The bot could not tell which item sits in which slot, so it touches nothing (status <Msg>Stopped: the inventory could not be read by slot</Msg>).</>],
          [<Msg red>Bag data looks wrong - melting is blocked.</Msg>, <>The bag reading does not make sense this moment (status <Msg>Stopped: bag data looks wrong</Msg>). It clears by itself.</>],
          [<Msg>Too many locked slots to guard safely - nothing melted.</Msg>, 'A pop-up: more locked items than one run can keep track of, so it melts nothing rather than protect only some. Unlock slots you no longer need.'],
          [<Msg red>Item list truncated - some items are not visible to the bot.</Msg>, 'You carry more items than the bot can read at once. Run it twice.'],
          [<Msg red>Stopped: the melt command does not look like it used to. Probably a game patch.</Msg>, <>A game update changed something, and the feature locked itself (status <Msg>Disabled: unexpected command layout</Msg>). Wait for a bot update.</>],
        ]}
      />

      <Callout type="info">
        Locked slots are shared with{' '}
        <button onClick={() => navigate('auto-sell')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Auto Sell</button>{' '}
        &mdash; a slot locked on{' '}
        <button onClick={() => navigate('bag')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">FARM &rsaquo; Bag</button>{' '}
        is skipped here as well. Your melt settings can be saved in a{' '}
        <button onClick={() => navigate('farm-profiles')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">farm profile</button>;
        loading one never switches Auto melt on, never touches Dry run and never changes your keys.
        The <em>Open cubes automatically</em> switch, however, is saved with the profile, so loading
        a profile can turn cube opening on.
      </Callout>
    </div>
  );
}

export default PageAutoMelt;
